import React from 'react';

/**
 * Hero — credibility-first, staggered entrance on load.
 * Entrances use .sk-enter (CSS animation, reduced-motion safe).
 */
const HeroSectionTailwind = ({ t }) => (
    <section
        id="hero"
        className="relative overflow-hidden bg-mist pt-[104px] pb-16 md:pt-[128px] md:pb-24"
    >
        {/* Ambient background: two soft brand blobs */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="sk-float absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-primary-soft blur-3xl opacity-80" />
            <div className="sk-float absolute -bottom-32 -left-24 w-[380px] h-[380px] rounded-full bg-secondary/10 blur-3xl" style={{ animationDelay: '2.5s' }} />
        </div>

        <div className="sk-container relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-14 items-center">
                {/* Copy */}
                <div>
                    <p className="sk-eyebrow sk-enter" style={{ '--enter-delay': '0ms' }}>
                        {t('credential-nutritionist')}
                    </p>

                    <h1
                        className="sk-enter mt-4 font-heading font-bold text-primary-deep leading-[1.12] text-[clamp(2rem,5vw,3.4rem)] max-w-[16ch]"
                        style={{ '--enter-delay': '90ms' }}
                    >
                        {t('hero-title')}
                    </h1>

                    <p
                        className="sk-enter mt-4 font-heading font-medium text-primary text-[clamp(1.05rem,2vw,1.3rem)]"
                        style={{ '--enter-delay': '180ms' }}
                    >
                        {t('hero-subtitle')}
                    </p>

                    <p
                        className="sk-enter mt-4 text-ink-soft text-[1.05rem] leading-relaxed max-w-[56ch]"
                        style={{ '--enter-delay': '260ms' }}
                    >
                        {t('hero-description')}
                    </p>

                    {/* CTAs */}
                    <div className="sk-enter mt-8 flex flex-wrap gap-3.5" style={{ '--enter-delay': '340ms' }}>
                        <a href="#contact" className="sk-btn sk-btn--primary">
                            <i className="fas fa-calendar-check text-[0.9rem]" aria-hidden="true" />
                            {t('hero-cta-primary')}
                        </a>
                        <a href="#about" className="sk-btn sk-btn--ghost">
                            {t('hero-cta-secondary')}
                        </a>
                    </div>

                    {/* Stats strip — editorial, divider-separated */}
                    <dl
                        className="sk-enter mt-10 flex flex-wrap items-stretch gap-y-4 border-t border-line pt-6"
                        style={{ '--enter-delay': '420ms' }}
                    >
                        {[
                            { number: '500+', label: t('stat-clients') },
                            { number: '10+', label: t('stat-experience') },
                            { number: '95%', label: t('stat-success') },
                        ].map((stat, i) => (
                            <div
                                key={stat.label}
                                className={`flex-1 min-w-[110px] ${i > 0 ? 'border-s border-line ps-5' : ''}`}
                            >
                                <dt className="order-2 text-[0.78rem] font-semibold uppercase tracking-wider text-ink-soft mt-1">
                                    {stat.label}
                                </dt>
                                <dd className="m-0 font-heading font-bold text-primary text-[1.7rem] leading-none">
                                    {stat.number}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                {/* Portrait */}
                <div className="sk-enter relative mx-auto w-full max-w-[420px]" style={{ '--enter-delay': '200ms' }}>
                    {/* Organic frame */}
                    <div className="relative overflow-hidden rounded-[38%_62%_55%_45%/45%_48%_52%_55%] border-[6px] border-white shadow-[var(--sk-shadow-3)] bg-primary-soft">
                        <img
                            src="/sara1.png"
                            alt="RD. Sara Khedr, Clinical Dietitian"
                            width="420"
                            height="520"
                            className="w-full h-auto object-cover"
                        />
                    </div>

                    {/* Credential badge */}
                    <div className="absolute bottom-4 start-2 sm:start-0 flex items-center gap-2.5 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-[var(--sk-shadow-2)] border border-line">
                        <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-soft text-primary">
                            <i className="fas fa-user-md" aria-hidden="true" />
                        </span>
                        <span className="leading-tight">
                            <span className="block font-heading font-semibold text-primary-deep text-[0.9rem]">RD. Sara Khedr</span>
                            <span className="block text-[0.75rem] text-ink-soft">{t('nav-subtitle')}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

export default HeroSectionTailwind;
